/**
 * Resumes page — list of generated resumes.
 *
 * Features:
 *  - Card per resume with status badge, posting title, company, date
 *  - Global polling (via useGlobalResumePolling in App) updates status in real-time
 *  - Click "View" → opens /resumes/:id (full side-by-side view)
 *  - Delete per resume with inline confirmation
 *  - Always-visible navigation: Capabilities / Postings / Resumes
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText,
  Loader2,
  CheckCircle2,
  XCircle,
  Clock,
  Trash2,
  Eye,
  ArrowRight,
} from 'lucide-react';
import { resumeApi, authApi } from '@/lib/api';
import type { Resume } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Navigation } from '@/components/Navigation';
import { TemplateSection } from '@/components/TemplateSection';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function StatusBadge({ status }: { status: Resume['status'] }) {
  switch (status) {
    case 'pending':
      return (
        <Badge variant="outline" className="gap-1 text-muted-foreground">
          <Clock className="h-3 w-3" />
          Queued
        </Badge>
      );
    case 'generating':
      return (
        <Badge variant="outline" className="gap-1 text-blue-600 border-blue-300">
          <Loader2 className="h-3 w-3 animate-spin" />
          Generating…
        </Badge>
      );
    case 'completed':
      return (
        <Badge variant="outline" className="gap-1 text-green-700 border-green-300">
          <CheckCircle2 className="h-3 w-3" />
          Ready
        </Badge>
      );
    case 'failed':
      return (
        <Badge variant="outline" className="gap-1 text-destructive border-destructive/40">
          <XCircle className="h-3 w-3" />
          Failed
        </Badge>
      );
  }
}

// ── Resume card ────────────────────────────────────────────────────────────────

function ResumeCard({
  resume,
  onDelete,
}: {
  resume: Resume;
  onDelete: (id: string) => void;
}) {
  const navigate = useNavigate();
  const [confirmDelete, setConfirmDelete] = useState(false);

  const title = resume.postingTitle ?? '(Untitled Posting)';
  const company = resume.postingCompany ?? '—';
  const score = resume.confidenceSummary?.overallScore;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          {/* Left: title + meta */}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={resume.status} />
              {score !== undefined && resume.status === 'completed' && (
                <Badge
                  variant="secondary"
                  className={`text-xs ${
                    score >= 75 ? 'text-green-700' : score >= 50 ? 'text-yellow-700' : 'text-red-700'
                  }`}
                >
                  {score}% match
                </Badge>
              )}
            </div>
            <h3 className="mt-1.5 truncate text-base font-semibold leading-snug">{title}</h3>
            <p className="text-sm text-muted-foreground">{company}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">Generated {formatDate(resume.createdAt)}</p>
          </div>

          {/* Right: actions */}
          <div className="flex shrink-0 items-center gap-1">
            {resume.status === 'completed' && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5 text-xs"
                onClick={() => navigate(`/resumes/${resume.id}`)}
              >
                <Eye className="h-3.5 w-3.5" />
                View
              </Button>
            )}

            {!confirmDelete ? (
              <button
                onClick={() => setConfirmDelete(true)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                title="Delete resume"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            ) : (
              <div className="flex items-center gap-1 rounded-md border border-destructive/30 bg-destructive/5 px-2 py-1 text-xs">
                <span className="text-destructive">Delete?</span>
                <button
                  onClick={() => onDelete(resume.id)}
                  className="font-semibold text-destructive hover:underline"
                >
                  Yes
                </button>
                <span className="text-muted-foreground">/</span>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="text-muted-foreground hover:underline"
                >
                  No
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Error for failed resumes */}
        {resume.status === 'failed' && resume.errorMessage && (
          <div className="mt-2 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
            {resume.errorMessage}
          </div>
        )}
      </CardHeader>

      {/* Confidence summary preview (completed only) */}
      {resume.status === 'completed' && resume.confidenceSummary && (
        <CardContent className="pt-0">
          <div className="grid gap-3 text-sm sm:grid-cols-2">
            {resume.confidenceSummary.strengths.length > 0 && (
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-green-700">
                  Strengths
                </p>
                <ul className="space-y-0.5">
                  {resume.confidenceSummary.strengths.slice(0, 3).map((s, i) => (
                    <li key={i} className="flex gap-2 text-xs text-muted-foreground">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-green-500" />
                      {s}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {resume.confidenceSummary.gaps.length > 0 && (
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-yellow-700">
                  Gaps
                </p>
                <ul className="space-y-0.5">
                  {resume.confidenceSummary.gaps.slice(0, 3).map((g, i) => (
                    <li key={i} className="flex gap-2 text-xs text-muted-foreground">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-yellow-500" />
                      {g}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <button
            onClick={() => navigate(`/resumes/${resume.id}`)}
            className="mt-3 flex items-center gap-1 text-xs text-primary hover:underline"
          >
            View full resume
            <ArrowRight className="h-3 w-3" />
          </button>
        </CardContent>
      )}
    </Card>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function ResumesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Auth check
  const { data: auth, isLoading: authLoading } = useQuery({
    queryKey: ['me'],
    queryFn: authApi.me,
    retry: false,
  });

  if (!authLoading && !auth) {
    navigate('/login');
    return null;
  }

  // Resumes list (polling happens globally via useGlobalResumePolling)
  const { data: resumes = [], isLoading } = useQuery({
    queryKey: ['resumes'],
    queryFn: resumeApi.list,
    enabled: !!auth,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: resumeApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['resumes'] }),
  });

  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Persistent navigation */}
      <Navigation />

      {/* Body */}
      <main className="mx-auto max-w-4xl px-4 py-8">
        {/* Page title */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Generated Resumes</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Tailored resumes matched to specific job postings.
            </p>
          </div>
          <Button variant="outline" onClick={() => navigate('/postings')}>
            Add Posting
          </Button>
        </div>

        {/* Template management section */}
        <TemplateSection />

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : resumes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <FileText className="mb-4 h-12 w-12 text-muted-foreground/40" />
            <h2 className="text-lg font-semibold">No resumes yet</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Go to a job posting and click "Generate Resume" to create one.
            </p>
            <Button className="mt-4" onClick={() => navigate('/postings')}>
              Browse Postings
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {resumes.map((resume) => (
              <ResumeCard
                key={resume.id}
                resume={resume}
                onDelete={(id) => deleteMutation.mutate(id)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
